import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { CameraOff, RefreshCw, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/cn';

/*
  ═════════════════════════════════════════════════════════════════════════════
  READING A QR CODE OFF A PARENT'S PHONE

  Why this exists in the browser at all
  ─────────────────────────────────────
  The Expo gate app has scanned QR since the beginning; the web gate did not, so
  a school running SafePass on a laptop or a borrowed tablet had to read six
  digits aloud for every family. Same payload, same flow, one fewer thing that
  depends on which app the school installed.

  Why jsQR and not the browser's own BarcodeDetector
  ──────────────────────────────────────────────────
  `BarcodeDetector` is faster and free, and it exists in Chromium only — not in
  Safari, which is what an iPad at a gate runs. Supporting both would mean two
  decode paths, one of which nobody here would ever exercise, and the untested
  one would be the one running on the day it mattered. One path, everywhere.

  THE CONSTRAINT THAT WILL BITE
  ─────────────────────────────
  `navigator.mediaDevices` does not exist on an insecure origin. A gate served
  over plain http:// — including over an IP address — cannot open a camera at
  all, in any browser, and no permission prompt will ever appear. That is a
  browser rule, not something this component can work around, so it says so in
  words instead of showing a button that does nothing.
  ═════════════════════════════════════════════════════════════════════════════
*/

type State =
  | { name: 'starting' }
  | { name: 'scanning' }
  /*
    `detail` is the browser's own error, shown verbatim.

    Without it every camera failure read as one generic sentence, and an officer
    — or whoever they ring — had no way to tell "another app is holding the
    camera" from "this device has none" from "the permission is blocked at the
    OS level". Three different fixes, one message.
  */
  | { name: 'blocked'; reason: 'insecure' | 'denied' | 'no_camera' | 'failed'; detail?: string };

const BLOCKED: Record<
  Extract<State, { name: 'blocked' }>['reason'],
  { title: string; body: string }
> = {
  insecure: {
    title: 'The camera needs a secure connection',
    body: 'Browsers only allow camera access over https:// (or on localhost). This page is on plain http, so no browser will open the camera here. Type the six digits instead, and ask whoever set up the server for an https address.',
  },
  denied: {
    title: 'Camera permission was refused',
    body: 'Allow the camera for this site in the browser’s address bar, then try again. You can carry on with the six digits in the meantime.',
  },
  no_camera: {
    title: 'No camera on this device',
    body: 'Nothing to scan with here. Use the six-digit code.',
  },
  failed: {
    title: 'The camera would not start',
    body: 'Another app may be holding it. Close anything else using the camera, or just use the six-digit code.',
  },
};

export function QrScanner({
  onScan,
  paused = false,
  className,
}: {
  /** Called with the raw QR text. Fires once per distinct code. */
  onScan: (text: string) => void;
  /** Stops decoding without tearing the camera down — e.g. mid-request. */
  paused?: boolean;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<State>({ name: 'starting' });
  /* Bumped to re-run the effect: a retry after the person has fixed the
     permission, or a switch to a different camera. */
  const [attempt, setAttempt] = useState(0);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');

  /* Kept in refs so the decode loop never restarts when they change — a loop
     that restarts drops the camera for a frame and flickers. */
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const lastRef = useRef('');
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let frame = 0;
    let stopped = false;

    const stop = () => {
      stopped = true;
      cancelAnimationFrame(frame);
      stream?.getTracks().forEach((t) => t.stop());
    };

    const tick = () => {
      frame = requestAnimationFrame(tick);
      if (pausedRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      /* HAVE_CURRENT_DATA is enough to copy a frame. Waiting for
         HAVE_ENOUGH_DATA — which some browsers never report for a live camera,
         because there is no buffered future to have — can mean the loop spins
         forever on a working stream. */
      if (!video || !canvas || video.readyState < 2 || !video.videoWidth) return;

      /* Decode at a fixed width. A 1080p frame is four times the pixels for no
         extra accuracy on a QR that fills a third of the view, and on a cheap
         tablet it is the difference between a smooth preview and a slideshow. */
      const width = 480;
      const height = Math.round((video.videoHeight / video.videoWidth) * width) || 360;
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, width, height);

      const image = ctx.getImageData(0, 0, width, height);
      /* Both polarities. Our own QR is dark-on-white, but a phone screen behind
         glare, or a parent who screenshotted it into a dark-mode note, can
         present the inverse — and refusing to read it is indistinguishable, to
         the officer holding the queue, from the scanner being broken. */
      const found = jsQR(image.data, width, height, { inversionAttempts: 'attemptBoth' });
      if (!found?.data) return;

      /* The same code sits in front of the lens for a second or more, which is
         thirty-odd frames. Only the first one counts. */
      if (found.data === lastRef.current) return;
      lastRef.current = found.data;
      onScanRef.current(found.data);
    };

    const start = async () => {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        setState({ name: 'blocked', reason: 'insecure' });
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: deviceId
            ? { deviceId: { exact: deviceId }, width: { ideal: 1280 } }
            : /* The back camera: an officer points the device at the parent's
                 phone, not at themselves. `ideal`, not `exact`, so a laptop
                 with only a front camera still works. */
              { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
          audio: false,
        });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        /* iOS Safari refuses to play an inline video without both of these. */
        video.setAttribute('playsinline', 'true');
        video.muted = true;
        await video.play();
        setState({ name: 'scanning' });
        frame = requestAnimationFrame(tick);

        /* Only after permission is granted do device labels exist, so the list
           is read here rather than up front. A gate laptop with an external
           webcam plugged in often picks the wrong one, and without this the
           officer has no way to say so. */
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          setCameras(devices.filter((d) => d.kind === 'videoinput'));
        } catch {
          /* Not being able to list cameras does not stop the one that works. */
        }
      } catch (err) {
        const name = (err as DOMException).name;
        const message = (err as DOMException).message;
        setState({
          name: 'blocked',
          reason:
            name === 'NotAllowedError' || name === 'SecurityError'
              ? 'denied'
              : name === 'NotFoundError' || name === 'OverconstrainedError'
                ? 'no_camera'
                : 'failed',
          detail: [name, message].filter(Boolean).join(': '),
        });
      }
    };

    void start();
    return stop;
  }, [attempt, deviceId]);

  if (state.name === 'blocked') {
    const { title, body } = BLOCKED[state.reason];
    const insecure = state.reason === 'insecure';
    return (
      <div
        className={cn(
          'flex items-start gap-2.5 rounded-lg border px-3.5 py-3',
          insecure
            ? 'border-warning/30 bg-warning/[0.08]'
            : 'border-border bg-muted/40',
          className,
        )}
      >
        {insecure ? (
          <ShieldAlert size={16} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
        ) : (
          <CameraOff size={16} className="mt-[1px] flex-shrink-0 text-muted-foreground" />
        )}
        <div>
          <p
            className={cn(
              'text-[12.5px] font-bold',
              insecure ? 'text-[hsl(var(--record-ink-warning))]' : 'text-foreground',
            )}
          >
            {title}
          </p>
          <p
            className={cn(
              'mt-0.5 text-[11px] leading-relaxed',
              insecure ? 'text-[hsl(var(--record-ink-warning))]/85' : 'text-muted-foreground',
            )}
          >
            {body}
          </p>

          {/* The browser's own words. Nobody at a gate can act on "the camera
              would not start", and whoever they ring certainly cannot. */}
          {state.detail && (
            <p className="mt-1.5 break-words font-mono text-[10px] text-muted-foreground/70">
              {state.detail}
            </p>
          )}

          {/* Permission is fixed in the address bar, outside this page, so
              there has to be a way back in without reloading and losing the
              officer's place in a queue. */}
          {!insecure && (
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-[11px] font-bold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <RefreshCw size={12} />
              Try the camera again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative overflow-clip rounded-lg bg-black', className)}>
      <video ref={videoRef} className="block h-full w-full object-cover" playsInline muted />
      <canvas ref={canvasRef} className="hidden" />

      {/* A frame to aim with. Purely a target — decoding uses the whole image,
          so a code slightly outside it still reads. */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-[58%] aspect-square rounded-lg border-2 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
      </div>

      {/* Only when there is a choice to make. A gate laptop with an external
          webcam plugged in routinely opens the wrong one, and the officer is
          left pointing a screen at a parent's phone with nothing happening. */}
      {cameras.length > 1 && (
        <select
          aria-label="Which camera"
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
          className="absolute right-2 top-2 max-w-[60%] truncate rounded-md border border-white/25 bg-black/60 px-2 py-1 text-[11px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <option value="">Default camera</option>
          {cameras.map((c, i) => (
            <option key={c.deviceId} value={c.deviceId}>
              {c.label || `Camera ${i + 1}`}
            </option>
          ))}
        </select>
      )}

      <p className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/55 px-3 py-1.5 text-center text-[11px] font-semibold text-white">
        {state.name === 'starting'
          ? 'Starting the camera…'
          : "Point at the QR code on the parent's phone"}
      </p>
    </div>
  );
}
